export const profileFields = ['name', 'sex', 'birthdate', 'age', 'ageReferenceDate', 'ethnicity', 'height', 'weight', 'bmi', 'remarks']
// coremanager/cohort_basic_info.py profile_dto always emits nullable name/sex.
// Missing demographics are valid; a null/scalar/array/envelope/empty object is not
// a profile response. Keep this minimum shape check independent of field values.
export function isBasicInfoResponse(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const prototype = Object.getPrototypeOf(value)
  return (prototype === Object.prototype || prototype === null) &&
    ['name', 'sex'].every(key => Object.hasOwn(value, key) && (value[key] === null || typeof value[key] === 'string'))
}
export const profileExportFields = profileFields.filter(key => key !== 'name' && key !== 'ageReferenceDate')
export const emptyBasicInfo = () => Object.fromEntries(profileFields.map(key => [key, null]))
export const shanghaiToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
export function profileDraft(info = {}, today = shanghaiToday()) {
  const draft = Object.fromEntries(profileFields.filter(key => key !== 'bmi').map(key => [key, info?.[key] ?? '']))
  draft.mode = info?.birthdate ? 'dob' : 'age'
  if (draft.mode === 'dob') { draft.age = ''; draft.ageReferenceDate = today }
  else draft.ageReferenceDate ||= today
  return draft
}
export function derivedBMI(height, weight) {
  const h = Number(height), w = Number(weight)
  return h > 0 && w > 0 ? Math.round(w / (h / 100) ** 2 * 100) / 100 : null
}
const text = value => String(value ?? '').trim() || null
const number = value => value === '' || value === null || value === undefined ? null : Number(value)
// Only editable demographics belong in a basic-info PATCH; saved remarks stay read-only here.
function writable(draft) {
  const age = draft.mode === 'age' ? number(draft.age) : null
  return { name: text(draft.name), sex: text(draft.sex), ethnicity: text(draft.ethnicity),
    height: number(draft.height), weight: number(draft.weight), birthdate: draft.mode === 'dob' ? text(draft.birthdate) : null,
    age, ageReferenceDate: age !== null ? text(draft.ageReferenceDate) : null }
}
export function basicInfoPatch(original, draft, today = shanghaiToday()) {
  const before = writable(original), after = writable(draft)
  // Keep historical age metadata on unrelated edits; a newly entered age is observed today.
  after.ageReferenceDate = after.age === before.age ? before.ageReferenceDate : after.age === null ? null : today
  const patch = Object.fromEntries(Object.entries(after).filter(([key, value]) => value !== before[key]))
  // Send the internal observation date together with an age write.
  if (patch.age !== undefined && patch.age !== null) patch.ageReferenceDate = after.ageReferenceDate
  return patch
}
export const draftChanged = (original, draft) => Object.keys(original).some(key => String(original[key] ?? '') !== String(draft[key] ?? ''))
export function validCalendarDate(value, today = shanghaiToday()) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value < '1900-01-01' || value > today) return false
  const date = new Date(`${value}T00:00:00Z`)
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
}
// Returns localization keys; API validation remains authoritative.
export function validateBasicInfo(draft, today = shanghaiToday()) {
  for (const [key, max] of [['name', 128], ['ethnicity', 64], ['remarks', 2000]]) {
    const value = String(draft[key] ?? '')
    if (Array.from(value).length > max || (key === 'remarks' ? /\x00/ : /[\x00-\x1f\x7f]/).test(value)) return 'cohort_profile_invalid_text'
  }
  if (draft.sex && !['男', '女', '未知'].includes(draft.sex)) return 'cohort_profile_invalid_sex'
  for (const [key, low, high] of [['height', 30, 250], ['weight', 1, 500], ...(draft.mode === 'age' ? [['age', 0, 130]] : [])]) {
    const raw = draft[key]
    if (raw !== '' && raw !== null && raw !== undefined && (!Number.isInteger(Number(raw)) || Number(raw) < low || Number(raw) > high)) return 'cohort_profile_invalid_number'
  }
  if (draft.mode === 'dob' && draft.birthdate) {
    if (!validCalendarDate(draft.birthdate, today)) return 'cohort_profile_invalid_date'
    const age = Number(today.slice(0, 4)) - Number(draft.birthdate.slice(0, 4)) - (today.slice(5) < draft.birthdate.slice(5) ? 1 : 0)
    if (age > 130) return 'cohort_profile_invalid_date'
  }
  if (draft.mode === 'age' && draft.age !== '' && draft.age !== null && !validCalendarDate(draft.ageReferenceDate, today)) return 'cohort_profile_invalid_date'
  return ''
}
export function basicInfoItems(info, t) {
  const p = info || emptyBasicInfo(), display = value => value ?? '--'
  const sex = p.sex === '男' ? t('gender_male') : p.sex === '女' ? t('gender_female') : p.sex === '未知' ? t('cohort_profile_unknown') : display(p.sex)
  return [
    { label: t('name'), value: display(p.name) }, { label: t('gender'), value: sex },
    { label: t('birthdate'), value: display(p.birthdate) }, { label: t('age'), value: p.age === null ? '--' : `${p.age}${t('years_suffix')}` },
    { label: t('ethnicity'), value: display(p.ethnicity) }, { label: t('height'), value: p.height === null ? '--' : `${p.height} cm` },
    { label: t('weight'), value: p.weight === null ? '--' : `${p.weight} kg` }, { label: 'BMI', value: display(p.bmi) }
  ]
}
